// src/services/auth.service.ts
import axios, { AxiosInstance } from 'axios';
import { LoginRequest, RegisterRequest, AuthResponse, User } from '../types';

class AuthService {
    private api: AxiosInstance;
    private tokenKey = 'access_token';
    private refreshTokenKey = 'refresh_token';
    private userKey = 'user_data';

    constructor() {
        this.api = axios.create({
            baseURL: (process.env.REACT_APP_API_URL || 'http://localhost:3000').replace(/\/+$/, ''),
            headers: {
                'Content-Type': 'application/json',
            },
        });

        // Interceptor để thêm token vào request
        this.api.interceptors.request.use((config) => {
            const token = this.getAccessToken();
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        });

        // Interceptor để refresh token khi hết hạn
        this.api.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;
                if (error.response?.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;
                    try {
                        const newToken = await this.refreshToken();
                        if (newToken) {
                            this.setAccessToken(newToken);
                            originalRequest.headers.Authorization = `Bearer ${newToken}`;
                            return this.api(originalRequest);
                        }
                    } catch (refreshError) {
                        this.logout();
                        window.location.href = '/login';
                        return Promise.reject(refreshError);
                    }
                }
                return Promise.reject(error);
            }
        );
    }

    async login(credentials: LoginRequest): Promise<AuthResponse> {
        try {
            const response = await this.api.post<any>('/auth/login', credentials);

            const { accessToken, refreshToken } = response.data;

            if (accessToken) localStorage.setItem('access_token', accessToken);
            if (refreshToken) localStorage.setItem('refresh_token', refreshToken);


            let user: User | null = null;

            // 1) Try response data first
            const payloadUser = response.data?.user || response.data?.data?.user || response.data?.data;
            if (payloadUser && payloadUser._id) {
                user = payloadUser as User;
            }

            // 2) Fallback to /users/me
            if (!user) {
                try {
                    const userResponse = await this.api.get<any>('/users/me', {
                        headers: { Authorization: `Bearer ${accessToken}` }
                    });
                    const userData = userResponse.data;
                    user = (userData && 'data' in userData ? userData.data : userData) as User;
                } catch (getUserError) {
                    console.warn('⚠️ Failed to get user data from /users/me:', getUserError);
                }
            }

            if (user) {
                localStorage.setItem('user_data', JSON.stringify(user));
            }

            return {
                accessToken,
                refreshToken,
                user: user || null,
            };
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message || 'Login failed');
        }
    }

    async register(userData: RegisterRequest): Promise<AuthResponse> {
        try {
            const response = await this.api.post<AuthResponse>('/auth/register', userData);
            const { accessToken, refreshToken, user } = response.data;
            if (!accessToken || !refreshToken || !user?._id) {
                throw new Error('API đăng ký chưa trả đủ thông tin để đăng nhập tự động.');
            }

            this.setTokens(accessToken, refreshToken);
            this.setUser(user);
            return {
                accessToken,
                refreshToken,
                user,
            };
        } catch (error: any) {
            if (error instanceof Error && !axios.isAxiosError(error)) {
                throw error;
            }
            throw new Error(error.response?.data?.message || 'Registration failed');
        }
    }

    async refreshToken(): Promise<string | null> {
        try {
            const refreshToken = this.getRefreshToken();
            if (!refreshToken) return null;

            const response = await this.api.post<{ accessToken: string; refreshToken: string }>(
                '/auth/refresh_token',
                { refreshToken }
            );

            const { accessToken, refreshToken: newRefreshToken } = response.data;
            this.setTokens(accessToken, newRefreshToken);

            return accessToken;
        } catch (error) {
            return null;
        }
    }

    async verifyToken(token: string): Promise<boolean> {
        try {
            await this.api.get('/users/me', {
                headers: { Authorization: `Bearer ${token}` }
            });
            return true;
        } catch (error) {
            return false;
        }
    }

    async getCurrentUser(): Promise<User | null> {
        try {
            const response = await this.api.get<User>('/users/me');
            const user = response.data;
            this.setUser(user);
            return user;
        } catch (error) {
            return null;
        }
    }

    logout(): void {
        localStorage.removeItem(this.tokenKey);
        localStorage.removeItem(this.refreshTokenKey);
        localStorage.removeItem(this.userKey);
        window.dispatchEvent(new Event('auth:logout'));
    }

    private setTokens(accessToken: string, refreshToken: string): void {
        localStorage.setItem(this.tokenKey, accessToken);
        localStorage.setItem(this.refreshTokenKey, refreshToken);
    }

    private setAccessToken(token: string): void {
        localStorage.setItem(this.tokenKey, token);
    }

    getAccessToken(): string | null {
        return localStorage.getItem(this.tokenKey);
    }

    getRefreshToken(): string | null {
        return localStorage.getItem(this.refreshTokenKey);
    }

    private setUser(user: User): void {
        localStorage.setItem(this.userKey, JSON.stringify(user));
    }

    getUser(): User | null {
        const data = localStorage.getItem(this.userKey);
        if (data) {
            try {
                return JSON.parse(data);
            } catch {
                return null;
            }
        }
        return null;
    }

    isAuthenticated(): boolean {
        return !!this.getAccessToken() && !!this.getUser();
    }
}

export const authService = new AuthService();